<template>
    <form class="h-full w-full flex flex-col p-8" @submit.prevent="submitForm">
        <div class="grid grid-cols-1 gap-6">
            <div class="flex flex-col" :class="{ invalid: !title.isValid }">
                <label for="title">Title*</label>
                <input type="text" id="title" v-model.trim="title.val" placeholder="" @blur="clearValidity('title')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!title.isValid" class="h-4 text-xs text-red-600">
                        {{ title.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col">
                <label for="description">Description</label>
                <div class="h-32 rounded-lg">
                    <textarea id="description" v-model.trim="description.val"
                        placeholder="A detailed description of the training content, objectives, and what participants can expect to learn."
                        class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"></textarea>
                </div>
            </div>
            <div class="flex flex-col">
                <label for="trainerInformation">Trainer Information</label>
                <div class="mt-2">
                    <ckeditor :editor="editor" v-model="trainerInformation.val" :config="editorConfig"></ckeditor>
                </div>
            </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-4 gap-6 mt-6">
            <div class="flex flex-col" :class="{ invalid: !location.isValid }">
                <label for="location">Location*</label>
                <input type="text" id="location" v-model.trim="location.val" placeholder=""
                    @blur="clearValidity('location')" />
                <transition name="fade" mode="out-in">
                    <p v-if="!location.isValid" class="h-4 text-xs text-red-600">
                        {{ location.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !startDate.isValid }">
                <label for="startDate">Start Date*</label>
                <date-picker v-model:value="startDate.val" format="YYYY-MM-DD HH:mm:ss" type="datetime"
                    value-type="format" placeholder="Select Start Date"></date-picker>
                <transition name="fade" mode="out-in">
                    <p v-if="!startDate.isValid" class="h-4 text-xs text-red-600">
                        {{ startDate.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col" :class="{ invalid: !endDate.isValid }">
                <label for="endDate">End Date*</label>
                <date-picker v-model:value="endDate.val" format="YYYY-MM-DD HH:mm:ss" type="datetime"
                    value-type="format" placeholder="Select End Date"></date-picker>
                <transition name="fade" mode="out-in">
                    <p v-if="!endDate.isValid" class="h-4 text-xs text-red-600">
                        {{ endDate.msg }}
                    </p>
                </transition>
            </div>
            <div class="flex flex-col">
                <label for="duration">Duration</label>
                <input type="text" id="duration" v-model.trim="duration.val" placeholder="" />
            </div>
        </div>
        <div class="flex flex-row flex-wrap mt-6">
            <div class="w-full sm:w-1/4 sm:pr-4" :class="{ invalid: !entity.isValid }">
                <label for="entity">Entity*</label>
                <select v-model="entity.val" @blur="clearValidity('entity')" class="outline-none flex-grow ht-42">
                    <option value="">Select an Entity</option>
                    <option :value="item.entity" v-for="(item, index) in entities" :key="index">{{ item.entity }}
                    </option>
                </select>
                <transition name="fade" mode="out-in">
                    <p v-if="!entity.isValid" class="h-4 text-xs text-red-600">
                        {{ entity.msg }}
                    </p>
                </transition>
            </div>
            <div class="w-full sm:w-3/4 sm:pl-2 mt-6 sm:mt-0">
                <label for="prerequisites">Prerequisites</label>
                <div class="h-32 rounded-lg">
                    <textarea id="prerequisites" v-model.trim="prerequisites.val"
                        placeholder="Any requirements or prerequisites participants need to meet before attending the internship."
                        class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"></textarea>
                </div>
            </div>
            <div class="w-full mt-6">
                <label for="additionalInformation">Additional Information</label>
                <div class="h-32 rounded-lg">
                    <textarea id="additionalInformation" v-model.trim="additionalInformation.val"
                        placeholder="Any other relevant information, such as dress code, special instructions, or contact details for inquiries."
                        class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"></textarea>
                </div>
            </div>
            <div class="w-full mt-6">
                <div class="h-full flex justify-start items-center">
                    <input type="checkbox" id="sendEmail" v-model.trim="sendEmail" />
                    <label for="sendEmail" style="margin-bottom: 0px;" class="ml-2">Send Email</label>
                </div>
            </div>
        </div>
        <div class="w-full flex justify-center mt-8 pb-8">
            <div v-if="isLoading"
                class="w-36 border-2 font-medium border-ternary text-ternary bg-white rounded-lg px-4 py-2 cursor-pointer">
                <base-spinner-with-text class-list="h-6 w-6 text-ternary capitalize" :text="getLoadingBtnText"
                    text-class-list="text-ternary"></base-spinner-with-text>
            </div>
            <button v-else
                class="w-36 border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-4 py-2 uppercase text-sm cursor-pointer">{{
                    getBtnText }}</button>
        </div>
    </form>
    <div class="h-full flex justify-center items-center" v-if="isLoading">
        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import CKEditor from '@ckeditor/ckeditor5-vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
export default {
    name: "post-training-form",
    emits: ["created", "updated", "close"],
    props: ["mode", "selectedItem", "entities"],
    components: {
        ckeditor: CKEditor.component,
    },
    data() {
        return {
            title: {
                val: "",
                isValid: true,
                msg: "",
            },
            description: {
                val: "",
                isValid: true,
                msg: "",
            },
            trainerInformation: {
                val: "",
                isValid: true,
                msg: "",
            },
            editor: ClassicEditor,
            editorConfig: {
                placeholder: 'Name, bio, and credentials of the trainer leading the internship.',
            },
            location: {
                val: "",
                isValid: true,
                msg: "",
            },
            startDate: {
                val: null,
                isValid: true,
                msg: "",
            },
            endDate: {
                val: null,
                isValid: true,
                msg: "",
            },
            entity: {
                val: "",
                isValid: true,
                msg: "",
            },
            duration: {
                val: "",
                isValid: true,
                msg: "",
            },
            prerequisites: {
                val: "",
                isValid: true,
                msg: "",
            },
            additionalInformation: {
                val: "",
                isValid: true,
                msg: "",
            },
            sendEmail: false,
            formIsValid: true,
            isLoading: false,
        };
    },
    created() {
        if (this.mode == 'edit') {
            this.setFormData();
        }
    },
    computed: {
        getBtnText() {
            if (this.mode == 'create') {
                return "Create";
            } else {
                return "Update";
            }
        },
        getLoadingBtnText() {
            if (this.mode == 'create') {
                return "Creating...";
            } else {
                return "Updating...";
            }
        },
    },
    methods: {
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        validateForm() {
            this.formIsValid = true;

            if (this.title.val === "") {
                this.title.isValid = false;
                this.title.msg = "Title must not be empty.";
                this.formIsValid = false;
            }
            if (this.location.val === "") {
                this.location.isValid = false;
                this.location.msg = "Location must not be empty.";
                this.formIsValid = false;
            }
            if (this.startDate.val === null) {
                this.startDate.isValid = false;
                this.startDate.msg = "Start Date must not be empty.";
                this.formIsValid = false;
            }
            if (this.endDate.val === null) {
                this.endDate.isValid = false;
                this.endDate.msg = "End Date must not be empty.";
                this.formIsValid = false;
            }
            if (this.entity.val === null) {
                this.entity.isValid = false;
                this.entity.msg = "You must select an entity.";
                this.formIsValid = false;
            }

        },
        async submitForm() {
            this.validateForm();

            if (!this.formIsValid) {
                return;
            }

            const formData = {
                title: this.title.val,
                description: this.description.val,
                trainer_information: this.trainerInformation.val,
                location: this.location.val,
                start_date: this.startDate.val,
                end_date: this.endDate.val,
                duration: this.duration.val,
                entity: this.entity.val,
                prerequisites: this.prerequisites.val,
                additional_information: this.additionalInformation.val,
                send_email: this.sendEmail,
            };

            const CREATE_MUTATION = gql`
                mutation CreatePostTraining($input: CreatePostTrainingInput!) {
                    createPostTraining(input: $input) {
                        error
                        message
                    }
                }
            `;

            const UPDATE_MUTATION = gql`
                mutation UpdateEvent($token: String!, $input: CreatePostTrainingInput!) {
                    updatePostTraining(token: $token, input: $input) {
                        error
                        message
                    }
                }
            `;

            this.isLoading = true;
            let error_msg = null;
            let success_msg = null;

            try {
                await this.$apollo.mutate({
                    mutation: this.mode == 'create' ? CREATE_MUTATION : UPDATE_MUTATION,
                    variables: {
                        token: this.mode == 'edit' ? this.selectedItem.token : null,
                        input: formData
                    }
                }).then((data) => {
                    if (this.mode == 'create') {
                        if (data.data.createPostTraining.error) {
                            error_msg = data.data.createPostTraining.message;
                        } else {
                            success_msg = data.data.createPostTraining.message;
                        }
                    } else {
                        if (data.data.updatePostTraining.error) {
                            error_msg = data.data.updatePostTraining.message;
                        } else {
                            success_msg = data.data.updatePostTraining.message;
                        }
                    }
                }).catch((error) => {
                    error_msg = error;
                })

            } catch (error) {
                error_msg = error.message || "Failed to authenticate, try later.";
            }

            if (error_msg === null) {
                if (this.mode == 'create') {
                    this.$emit('created', success_msg);
                } else {
                    this.$emit('updated', success_msg);
                }
            } else {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        this.$emit('close');
                    }
                })
            }

            this.isLoading = false;

        },
        setFormData() {
            this.title.val = this.selectedItem.title;
            this.description.val = this.selectedItem.description;
            this.trainerInformation.val = this.selectedItem.trainer_information;
            this.location.val = this.selectedItem.location;
            this.startDate.val = this.selectedItem.start_date;
            this.endDate.val = this.selectedItem.end_date;
            this.duration.val = this.selectedItem.duration;
            this.entity.val = this.selectedItem.entity;
            this.prerequisites.val = this.selectedItem.prerequisites;
            this.additionalInformation.val = this.selectedItem.additional_information;
        },
    },
}
</script>

<style lang="scss" scoped>
label {
    @apply mb-1 block text-sm;
}

input[type="text"],
input[type="email"],
input[type="number"],
input[type="password"],
input[type="date"],
select {
    @apply block w-full border rounded-lg py-2 px-2;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="number"]:focus,
input[type="password"]:focus,
input[type="date"]:focus,
select:focus {
    outline: none;
    border-color: #394867;
    box-shadow: 0 0 0 2px #394867;
}

.invalid label {
    @apply text-red-600;
}

.invalid input,
.invalid select {
    border: 1px solid red;
}

input[type="checkbox"] {
    @apply w-auto;
}

.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    height: 0px;
}

.ht-30 {
    height: 30px;
}

.ht-42 {
    height: 42px;
}

.form-fade-enter-active,
.form-fade-leave-active {
    transition: all 0.5s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
    opacity: 0;
    height: 0px;
}
</style>

<style lang="scss">
.ck-content {
    height: 400px;
}

.ck-powered-by {
    display: none !important;
}

.mx-input {
    height: 42px !important;
}

.mx-datepicker {
    width: 100% !important;
}
</style>
